import { useMemo, useState, type ReactElement } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Pencil, Search } from 'lucide-react'

import {
  CoaTemplateAccountsTable,
  ImportAccountsDialog,
  coaTemplateAccountsQuery,
  coaTemplateQuery,
  useDeleteCoaTemplateAccount,
  useImportExecute,
  useImportPreview,
  type CoaTemplateAccount,
} from '~/features/coa-templates'
import { LegacyModalSurface } from '~/components/overlays/ModalSurface'
import { countryName } from '~/lib/countries'
import type { AppError } from '~/lib/result'

/**
 * `/admin/finance/reference-accounts/$templateId` (index) — one template's
 * account list: manual add/edit/delete plus the Excel/CSV import flow.
 * Sibling `edit` and `accounts/*` routes nest under the pathless
 * `$templateId.tsx` layout, not this index — this only renders at the
 * exact `/reference-accounts/$templateId` URL.
 */
export const Route = createFileRoute(
  '/admin/finance/reference-accounts/$templateId/',
)({
  component: TemplateDetailPage,
})

const EMPTY_ACCOUNTS: CoaTemplateAccount[] = []

function TemplateDetailPage(): ReactElement {
  const { templateId } = Route.useParams()
  const navigate = useNavigate()

  const templateQuery = useQuery(coaTemplateQuery(templateId))
  const accountsQuery = useQuery(coaTemplateAccountsQuery(templateId))

  const [deleteTarget, setDeleteTarget] = useState<CoaTemplateAccount | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')

  const deleteAccount = useDeleteCoaTemplateAccount()
  const importPreview = useImportPreview()
  const importExecute = useImportExecute()

  const template = templateQuery.data
  const accounts = accountsQuery.data?.accounts ?? EMPTY_ACCOUNTS

  const trimmedSearch = searchInput.trim().toLowerCase()
  const filteredAccounts = useMemo(() => {
    if (!trimmedSearch) return accounts
    return accounts.filter(
      (account) =>
        account.code.toLowerCase().includes(trimmedSearch) ||
        account.name.toLowerCase().includes(trimmedSearch),
    )
  }, [accounts, trimmedSearch])

  if (templateQuery.isPending) {
    return (
      <div className="ka-page" aria-busy="true">
        <div className="ka-sk ka-sk--line-flex-2" />
      </div>
    )
  }
  if (templateQuery.error || !template) {
    return (
      <div className="ka-page">
        <div className="ka-card">
          <div className="ka-empty">
            <div className="ka-empty-title">Template not found</div>
            <Link to="/admin/finance/reference-accounts" className="ka-btn ka-btn--secondary ka-btn--sm">
              <ArrowLeft size={14} aria-hidden="true" /> Back to templates
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <button
            type="button"
            className="ka-btn ka-btn--ghost ka-btn--sm"
            onClick={() => void navigate({ to: '/admin/finance/reference-accounts' })}
          >
            <ArrowLeft size={14} aria-hidden="true" /> Back
          </button>
          <h1 className="ka-page-header-title">{template.name}</h1>
          <p className="ka-page-header-subtitle">
            {template.country_codes.map((code) => countryName(code)).join(', ')} (
            {template.country_codes.join(', ')})
            {template.fiscal_year_label ? ` · ${template.fiscal_year_label}` : ''}
            {template.fiscal_year_end_date ? ` · year end ${template.fiscal_year_end_date}` : ''}
            {' · '}
            {accounts.length} accounts
          </p>
        </div>
        <div className="ka-page-header-actions">
          <button
            type="button"
            className="ka-btn ka-btn--secondary"
            onClick={() =>
              void navigate({
                to: '/admin/finance/reference-accounts/$templateId/edit',
                params: { templateId },
              })
            }
          >
            <Pencil size={14} aria-hidden="true" /> Edit Template
          </button>
        </div>
      </header>

      <div className="ka-page-header-actions" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className="ka-btn ka-btn--secondary ka-btn--sm"
          onClick={() =>
            void navigate({
              to: '/admin/finance/reference-accounts/$templateId/accounts/new',
              params: { templateId },
            })
          }
        >
          + Add Account
        </button>
        <button
          type="button"
          className="ka-btn ka-btn--primary ka-btn--sm"
          onClick={() => {
            importPreview.reset()
            importExecute.reset()
            setImportOpen(true)
          }}
        >
          Import Spreadsheet
        </button>
      </div>

      <div className="ka-filter-strip" role="search" style={{ marginBottom: 12 }}>
        <label className="ka-filter-search" aria-label="Search accounts by code or name">
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search code or name…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </label>
      </div>

      <CoaTemplateAccountsTable
        accounts={filteredAccounts}
        isLoading={accountsQuery.isPending}
        error={accountsQuery.error}
        onEdit={(account) =>
          void navigate({
            to: '/admin/finance/reference-accounts/$templateId/accounts/$accountId/edit',
            params: { templateId, accountId: account.id },
          })
        }
        onDelete={(account) => {
          setDeleteError(null)
          setDeleteTarget(account)
        }}
        onAdd={() =>
          void navigate({
            to: '/admin/finance/reference-accounts/$templateId/accounts/new',
            params: { templateId },
          })
        }
        onImport={() => setImportOpen(true)}
        hasFilters={trimmedSearch.length > 0}
        onClearFilters={() => setSearchInput('')}
      />

      {deleteTarget ? (
        <ConfirmDeleteAccountDialog
          account={deleteTarget}
          isSubmitting={deleteAccount.isPending}
          serverError={deleteError}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() =>
            deleteAccount.mutate(
              { templateId, accountId: deleteTarget.id },
              {
                onSuccess: () => {
                  setDeleteTarget(null)
                  setDeleteError(null)
                },
                onError: (cause) => setDeleteError(errorToMessage(cause)),
              },
            )
          }
        />
      ) : null}

      {importOpen ? (
        <ImportAccountsDialog
          isPreviewing={importPreview.isPending}
          isImporting={importExecute.isPending}
          preview={importPreview.data ?? null}
          summary={importExecute.data ?? null}
          serverError={
            importPreview.error
              ? errorToMessage(importPreview.error)
              : importExecute.error
                ? errorToMessage(importExecute.error)
                : null
          }
          onClose={() => {
            setImportOpen(false)
            importPreview.reset()
            importExecute.reset()
          }}
          onPreview={(file) => importPreview.mutate({ templateId, file })}
          onConfirm={(validRows) =>
            importExecute.mutate({
              templateId,
              body: { rows: validRows as Parameters<typeof importExecute.mutate>[0]['body']['rows'] },
            })
          }
          onReset={() => importPreview.reset()}
        />
      ) : null}
    </div>
  )
}

function ConfirmDeleteAccountDialog({
  account,
  isSubmitting,
  serverError,
  onClose,
  onConfirm,
}: {
  readonly account: CoaTemplateAccount
  readonly isSubmitting: boolean
  readonly serverError: string | null
  readonly onClose: () => void
  readonly onConfirm: () => void
}): ReactElement {
  return (
    <LegacyModalSurface onClose={onClose} title="Delete account?" size="sm">
      <div className="ka-modal is-sm">
        <header className="ka-modal-head">
          <h2 className="ka-modal-head-title">Delete account?</h2>
        </header>
        <div className="ka-modal-body">
          <p>
            Remove <strong>{account.code} · {account.name}</strong> from this template?
            Other rows whose parent points at this code will be un-nested, not deleted.
          </p>
          {serverError ? (
            <div className="ka-banner is-error" role="alert" aria-live="assertive" style={{ marginTop: 12 }}>
              {serverError}
            </div>
          ) : null}
        </div>
        <footer className="ka-modal-foot">
          <button type="button" className="ka-btn ka-btn--ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            className="ka-btn ka-btn--destructive"
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Removing…' : 'Remove Account'}
          </button>
        </footer>
      </div>
    </LegacyModalSurface>
  )
}

function errorToMessage(cause: unknown): string {
  const appError = cause as AppError
  return appError?.message ?? 'Something went wrong. Please try again.'
}
